import type { ApiResponse } from '@playforge/common';
import { getSessionToken, resetPlatformToken } from './auth';
import { API_BASE_URL } from './config';

/** Error thrown when the API returns a failure envelope or a non-OK response. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Abort a request that hangs longer than this so a page never spins forever. */
const REQUEST_TIMEOUT_MS = 15_000;

async function sendRequest(method: string, path: string, body: unknown): Promise<Response> {
  const token = await getSessionToken();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal: controller.signal,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } finally {
    clearTimeout(timer);
  }
}

async function request<T>(method: string, path: string, body?: unknown, isRetry = false): Promise<T> {
  let response: Response;
  try {
    response = await sendRequest(method, path, body);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError(0, 'Request timed out — please try again');
    }
    throw new ApiError(0, 'Network error — could not reach the server');
  }

  // A 401 usually means the cached platform token expired. Evict it and retry
  // once so a fresh Shopify session token is exchanged — avoids a permanent
  // error state that previously required a full page reload to clear.
  if (response.status === 401 && !isRetry) {
    resetPlatformToken();
    return request<T>(method, path, body, true);
  }

  let payload: ApiResponse<T>;
  try {
    payload = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError(response.status, `Unexpected response (${response.status})`);
  }

  if (!payload.success) {
    throw new ApiError(response.status, payload.message, payload.code);
  }
  return payload.data;
}

/** Typed REST client. Every response conforms to the platform's envelope. */
export const apiClient = {
  get: <T>(path: string): Promise<T> => request<T>('GET', path),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>('PUT', path, body),
  del: <T>(path: string): Promise<T> => request<T>('DELETE', path),
};
